/**
 * APButton Component
 *
 * A comprehensive button component following Figma design system specifications.
 * Supports multiple sizes, variants, states, and accessibility features.
 *
 * @example
 * ```tsx
 * <APButton size="large" variant="primary" onClick={handleClick}>
 *   Click me
 * </APButton>
 *
 * <APButton
 *   size="medium"
 *   variant="secondary"
 *   leftIcon={<Icon />}
 *   loading={isLoading}
 * >
 *   Submit
 * </APButton>
 * ```
 */

import { forwardRef } from 'react';

import type { IAPButtonProps } from './types';

import './styles.scss';

import APSpinner from '../spinner';

/**
 * APButton - Production-ready button component
 *
 * Follows AstroPay design system and accessibility guidelines.
 *
 */
export const APButton = forwardRef<HTMLButtonElement, IAPButtonProps>(
  (
    {
      size = 'medium',
      variant = 'primary',
      children,
      leftIcon,
      rightIcon,
      loading = false,
      fullWidth = false,
      disabled = false,
      className = '',
      type = 'button',
      'data-testid': testId,
      ...rest
    },
    ref
  ) => {
    // Determine if button should be disabled
    const isDisabled = disabled || loading;

    // Map component API variants to panel.css class names
    const VARIANT_MAP: Record<string, string> = {
      primary:     'ap-btn--primary',
      secondary:   'ap-btn--secondary',
      tertiary:    'ap-btn--ghost',      // tertiary → ghost
      destructive: 'ap-btn--danger',    // destructive → danger
      transparent: 'ap-btn--ghost',     // transparent → ghost
      'transparent-black': 'ap-btn--ghost',
      text:        'ap-btn--link',       // text → link
      danger:      'ap-btn--danger',
      ghost:       'ap-btn--ghost',
      link:        'ap-btn--link',
    };

    // Map sizes — panel.css only has --sm and --lg, medium is the default (no modifier)
    const SIZE_MAP: Record<string, string | null> = {
      small:  'ap-btn--sm',
      medium: null,        // default — no size modifier
      large:  'ap-btn--lg',
    };

    const baseClasses = 'ap-btn';
    const sizeClass   = SIZE_MAP[size] ?? null;
    const variantClass = VARIANT_MAP[variant] ?? `ap-btn--${variant}`;

    const stateClasses = [
      fullWidth && 'ap-btn--block',
      loading && 'is-loading',
      isDisabled && !loading && 'ap-btn--disabled',
    ].filter(Boolean);

    const buttonClasses = [baseClasses, sizeClass, variantClass, ...stateClasses, className]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={buttonClasses}
        data-testid={testId}
        aria-label={loading ? `${children}` : undefined}
        {...rest}
      >
        {/* Loading spinner */}
        {loading && <APSpinner size={'small'} variant={'primary'} className="btn__spinner" />}

        {/* Left icon */}
        {!loading && leftIcon && <span className="ap-btn__left-icon">{leftIcon}</span>}

        {/* Button content */}
        <span className="ap-btn__content">{children}</span>

        {/* Right icon */}
        {!loading && rightIcon && <span className="ap-btn__right-icon">{rightIcon}</span>}
      </button>
    );
  }
);

APButton.displayName = 'APButton';

export type { IAPButtonProps };
export default APButton;
